import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const DocumentExampleModal = ({ document, isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pdfPages, setPdfPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Reset page and zoom when document changes
  useEffect(() => {
    setCurrentPage(0);
    setPdfPages([]);
    setError(null);
    setZoom(1);
  }, [document?.id]);

  // Load PDF pages if it's a PDF document
  useEffect(() => {
    if (isOpen && document?.isPdf && document?.pdfUrl) {
      loadPdfPages();
    }
  }, [isOpen, document]);

  const loadPdfPages = async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${baseUrl}/api/sample-pdf-pages`);
      if (response.ok) {
        const data = await response.json();
        // Prepend base URL to image paths
        const pagesWithFullUrls = (data.pages || []).map(page => ({
          ...page,
          image: `${baseUrl}${page.image}`
        }));
        setPdfPages(pagesWithFullUrls);
      } else {
        // Fallback: show single page with download option
        setPdfPages([{
          image: document.previewImage,
          description: 'Нажмите "Скачать PDF" для просмотра полного документа'
        }]);
      }
    } catch (err) {
      console.error('Error loading PDF pages:', err);
      // Fallback: show preview with download button
      setPdfPages([{
        image: document.previewImage,
        description: 'Нажмите "Скачать PDF" для просмотра полного документа'
      }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !document) return null;

  const pages = document.isPdf ? pdfPages : document.pages;
  const totalPages = pages.length;

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0));
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleDownloadPdf = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${baseUrl}${document.pdfUrl}`, '_blank');
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={onClose}
      data-testid="document-example-modal"
    >
      <div 
        className="relative w-full h-full flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          data-testid="close-document-modal"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Zoom controls */}
        <div className="absolute top-4 left-4 z-20 flex gap-2">
          <button
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Уменьшить"
          >
            <ZoomOut className="w-5 h-5 text-white" />
          </button>
          <div className="px-3 py-2 rounded-full bg-black/50 text-white text-sm font-medium">
            {Math.round(zoom * 100)}%
          </div>
          <button
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Увеличить"
          >
            <ZoomIn className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="relative flex-1 w-full overflow-auto flex items-center justify-center p-4">
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="text-center space-y-4">
                <Loader2 className="w-12 h-12 text-white/50 animate-spin mx-auto" />
                <p className="text-white/60">Загрузка страниц документа...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center">
              <div className="text-center space-y-4">
                <p className="text-red-400">{error}</p>
              </div>
            </div>
          ) : totalPages > 0 ? (
            <div className="relative flex items-center justify-center w-full h-full">
              {/* Navigation buttons */}
              {totalPages > 1 && (
                <>
                  <button
                    onClick={handlePrevPage}
                    className="absolute left-4 z-10 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={handleNextPage}
                    className="absolute right-4 z-10 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-all"
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </>
              )}

              {/* Document page */}
              <div className="flex flex-col items-center justify-center w-full h-full">
                <img
                  src={pages[currentPage]?.image}
                  alt={`Страница ${currentPage + 1}`}
                  className="max-w-full h-auto object-contain transition-transform duration-200"
                  style={{ 
                    transform: `scale(${zoom})`,
                    maxHeight: '90vh'
                  }}
                  data-testid="document-page-image"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <div className="text-center space-y-4">
                <p className="text-white/60">Страницы документа недоступны</p>
              </div>
            </div>
          )}
        </div>

        {/* Page counter */}
        {totalPages > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className="px-4 py-2 rounded-full bg-black/50 text-white text-sm font-medium">
              {currentPage + 1} / {totalPages}
            </div>
          </div>
        )}

        {/* Page indicators */}
        {totalPages > 1 && totalPages <= 10 && (
          <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 flex justify-center gap-2">
            {pages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentPage
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
                data-testid={`page-indicator-${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentExampleModal;
